import React, { useEffect, useState } from 'react';
import { Popover, Select, Typography } from 'antd';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { DictionaryType } from '../../common/types';
import { StyledButton } from '../../components/Button';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { ButtonIcon } from '../../icons/Icon';
import { getUrlWithId } from '../../utils/reactQuery';
import { ApiRoutesEnum } from '../../utils/routes';
import { ConsentTemplateList } from '../consents/ConsentTemplateList';

import { DictionaryItemsList } from './DictionaryItemsList';
import { RecruitmentStatusList } from './RecruitmentStatusList';
import { useGetDictionariesList } from './useGetDictionariesList';
import { useMergeDictionary } from './useMergeDictionary';

const ConvertIcon = styled(ButtonIcon).attrs({
  name: 'Convert',
})``;

const MainContainer = styled.div`
  padding: 1rem 2rem;
  overflow: hidden;
  min-height: 100vh;
`;

const DictionariesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 20%;
`;

const DictionaryItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
  max-height: calc(100vh - 100px);
  border-bottom: 1px solid #dbdbdb;
`;

const StyledListItem = styled.div`
  font-size: 16px;
  border-top: 1px solid #dbdbdb;
  border-right: 1px solid #dbdbdb;
  border-left: 1px solid #dbdbdb;
  background-color: white;
  padding: 1rem;

  &:last-of-type {
    border-bottom: 1px solid #dbdbdb;
  }
`;

const ClickableListItem = styled(StyledListItem)<{ isCurrent: boolean }>`
  cursor: pointer;
  background-color: ${(props) => (props.isCurrent ? 'black' : 'white')};
  color: ${(props) => (props.isCurrent ? 'white' : 'black')};
`;

const ListLabel = styled.div`
  font-size: 14px;
  color: #9e9e9e;
  padding-left: 5px;
  padding-bottom: 3px;
`;

export const DictionariesPage: React.FC = () => {
  const [isMerging, setIsMerging] = useState(false);
  const [itemsToMerge, setItemsToMerge] = useState<DictionaryType[]>([]);
  const [replaceMergeItem, setReplaceMergeItem] = useState<number | undefined>();

  const [currentDictionary, setCurrentDictionary] = useState('');
  const [openMergeInfo, setOpenMergeInfo] = useState(false);

  const { data: dictionaries } = useGetDictionariesList();
  const mergeDictionary = useMergeDictionary();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (dictionaries?.[0]) {
      setCurrentDictionary(dictionaries[0]);
    }
  }, [dictionaries]);

  useEffect(() => {
    if (itemsToMerge.length > 0) {
      setOpenMergeInfo(true);
    } else {
      setOpenMergeInfo(false);
    }
  }, [itemsToMerge.length]);

  const handleChooseDictionary = (dictionary: string) => {
    setItemsToMerge([]);
    setCurrentDictionary(dictionary);
    setIsMerging(false);
  };

  if (!dictionaries) return <></>;
  const allDictionaries = [...[...dictionaries, 'recruitment'].sort(), 'consents'];

  const handleAddToMergeList = (item: DictionaryType) => {
    if (itemsToMerge.includes(item)) {
      const filteredItems = itemsToMerge.filter((element) => element.id !== item.id);
      setItemsToMerge(filteredItems);
    } else {
      setItemsToMerge((itemsToMerge) => [...itemsToMerge, item]);
    }
  };

  const cancelMergeItems = () => {
    setItemsToMerge([]);
    setIsMerging(false);
  };

  const handleMergeItems = () => {
    mergeDictionary.mutate(
      {
        data: { ids: itemsToMerge.map((item) => item.id).filter((elm) => elm !== replaceMergeItem) },
        queryParams: { dictionaryName: currentDictionary, id: replaceMergeItem },
      },
      {
        onSuccess: () => {
          setItemsToMerge([]);
          setIsMerging(false);
          queryClient.invalidateQueries([getUrlWithId(ApiRoutesEnum.getDictionaries, currentDictionary)]);
        },
      }
    );
  };

  const options = itemsToMerge.map((item) => ({ value: item.value, content: item.id }));

  return (
    <MainContainer>
      <StyledColumn>
        <StyledRow justifyContent="space-between">
          <Typography.Title level={3}>Dictionaries</Typography.Title>

          {isMerging ? (
            <StyledRow justifyContent="flex-end">
              <Typography.Text>Items to merge: {itemsToMerge.length}</Typography.Text>
              <Popover
                placement="bottomRight"
                title="Confirmation"
                content={
                  <StyledColumn width="400px">
                    <div>{`Do you confirm merging ${itemsToMerge.length} items?`}</div>
                    <StyledColumn gap="0">
                      <div>List of items:</div>
                      {itemsToMerge.map((item) => (
                        <div key={item.id}>{item.value}</div>
                      ))}
                      <Select options={options} onSelect={(item, option) => setReplaceMergeItem(option.content)} />
                      <StyledRow justifyContent="flex-end" marginTop="10px">
                        <StyledButton type="default" onClick={cancelMergeItems}>
                          Cancel
                        </StyledButton>
                        <StyledButton type="primary" onClick={handleMergeItems}>
                          Confirm
                        </StyledButton>
                      </StyledRow>
                    </StyledColumn>
                  </StyledColumn>
                }
                open={openMergeInfo}
              >
                <StyledButton type="primary" icon={<ConvertIcon />}>
                  Merge items
                </StyledButton>
              </Popover>
            </StyledRow>
          ) : currentDictionary !== 'consents' ? (
            <StyledButton type="primary" icon={<ConvertIcon />} onClick={() => setIsMerging(true)}>
              Choose items to merge
            </StyledButton>
          ) : null}
        </StyledRow>

        <StyledRow gap="10px">
          <DictionariesListContainer>
            <ListLabel>Dictionaries</ListLabel>
            {allDictionaries.map((dictionary) => (
              <ClickableListItem
                isCurrent={dictionary === currentDictionary}
                onClick={() => handleChooseDictionary(dictionary)}
                key={dictionary}
              >
                {(dictionary.charAt(0).toUpperCase() + dictionary.slice(1)).replace('-', ' ')}
              </ClickableListItem>
            ))}
          </DictionariesListContainer>
          <DictionaryItemsContainer style={{ backgroundColor: '#F7FAFC', zIndex: 10, border: 'none' }}>
            {currentDictionary === 'recruitment' ? (
              <RecruitmentStatusList />
            ) : currentDictionary === 'consents' ? (
              <ConsentTemplateList />
            ) : (
              <DictionaryItemsList
                selected={itemsToMerge}
                withItemsSelect={isMerging}
                onClick={handleAddToMergeList}
                currentDictionary={currentDictionary}
              />
            )}
          </DictionaryItemsContainer>
        </StyledRow>
      </StyledColumn>
    </MainContainer>
  );
};
